import {
  bulkUpdateSpareParts,
  createSparePart,
  deleteSparePart,
  getSpareParts,
  refurbishSparePart,
  updateSparePart,
} from '@/services/rc.service'
import type { ISparePartsDashboardFilters } from '@/types/rc/filters'
import type { ISparePartBulkUpdateForm, ISparePartCreationForm, ISparePartUpdateForm } from '@/types/rc/forms'
import type { ISparePart } from '@/types/rc/spare-parts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { ComputedRef } from 'vue'
import { flow } from 'lodash'
import { bulkArchiveSpareParts, bulkDeleteSpareParts } from '@/services/rc/spare-parts.service'
import { NotifyErrorWrapper, NotifyFail, NotifySuccess } from '@/helpers/core/notifications'

// Price comes as string?
const ensureFloatPrice = (r: ISparePart) => ({ ...r, price: r.price ? parseFloat(r.price as unknown as string) : null })

// Map customer null to 0 (owned by us)
const ensureOwnedByCompanyAsZero = (r: ISparePart) => ({ ...r, customer: { ...r.customer, id: r.customer.id ?? 0 } })

/**
 * Determines an additional source_work_order field from either miner_used_on or miner_coming_from
 * (only one of those should be defined)
 */
const collapseSourceWorkOrder = (r: ISparePart) => ({
  ...r,
  source_work_order: r.miner_used_on ? r.miner_used_on.work_order_number : r.miner_coming_from?.work_order_number,
})

/**
 * Adjusts site format to match out site select's model value
 */
const formatSite = (r: ISparePart) => ({
  ...r,
  // @ts-expect-error there's a mismatch between site expected format ({ site_id, site_name }) and format received ({ id, name })
  site: { site_id: r.site.id, site_name: r.site.name },
})

const rectifySparePart = flow(formatSite, ensureFloatPrice, ensureOwnedByCompanyAsZero, collapseSourceWorkOrder)

export const useSpareParts = () => {
  const client = useQueryClient()

  /**
   * Create query for spare parts given a part type with filters.
   *
   * @param {IndividualPart} partType part type.
   * @param {ISparePartDashboard} filters part type endpoint filters.
   * @returns spare parts query for applicable part type and filters.
   */
  const getSparesQuery = (
    filters: ComputedRef<ISparePartsDashboardFilters>,
    queryConf?: ComputedRef<{ backgroundEnabled: boolean }>
  ) =>
    useQuery({
      queryKey: ['spare-parts', filters],
      queryFn: () => getSpareParts(filters.value).then((r) => ({ ...r, data: r.data.map(rectifySparePart) })),
      refetchOnMount: false,
      refetchOnWindowFocus: () => (queryConf ? queryConf.value.backgroundEnabled : true),
    })

  // No messages nor invalidation since it may get bulk-fired from the view
  const addSparesMutation = useMutation({
    mutationFn: (form: ISparePartCreationForm) =>
      // Map customer 0 to null (owned by us)
      createSparePart({ ...form, customer_id: form.customer_id == 0 ? null : form.customer_id }).then(rectifySparePart),
  })

  const updateSparesMutation = useMutation({
    mutationFn: (update: ISparePartUpdateForm) =>
      // Map customer 0 to null (owned by us)
      updateSparePart(update.id, { ...update, customer_id: update.customer_id == 0 ? null : update.customer_id }).then(
        rectifySparePart
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['spare-parts'] })
      NotifySuccess('Spare part updated')
    },
  })

  const deleteSparesMutation = useMutation({
    mutationFn: ({ id }: { id: number }) => deleteSparePart(id),
    onError: NotifyErrorWrapper('Failed to delete spare part'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['spare-parts'] })
      NotifySuccess('Spare part deleted')
    },
  })

  const refurbishSparesMutation = useMutation({
    mutationFn: ({ id }: { id: number }) => refurbishSparePart(id),
    onError: NotifyErrorWrapper('Failed to refurbish spare part'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['spare-parts'] })
      NotifySuccess('Spare part refurbished')
    },
  })

  const bulkDeleteSparePartsMutation = useMutation({
    mutationFn: (ids: number[]) => bulkDeleteSpareParts(ids),
    onError: NotifyErrorWrapper('Failed to bulk delete spare part'),
    onSuccess: (_data, variables) => {
      client.invalidateQueries({ queryKey: ['spare-parts'] })
      NotifySuccess(`${variables.length} spare parts deleted`)
    },
  })

  const bulkArchiveSparePartsMutation = useMutation({
    mutationFn: (ids: number[]) => bulkArchiveSpareParts(ids),
    onError: NotifyErrorWrapper('Failed to archive spare parts'),
    onSuccess: (_data, variables) => {
      client.invalidateQueries({ queryKey: ['spare-parts'] })
      NotifySuccess(`${variables.length} spare parts archived`)
    },
  })

  const deleteManySparesMutation = useMutation({
    mutationFn: async ({ parts }: { parts: ISparePart[] }) =>
      Promise.all(
        parts.map(async (part) => {
          try {
            await deleteSparesMutation.mutateAsync({ id: part.id! })
            return part.id!
          } catch (e: any) {
            NotifyFail(`Failed to delete part ${part.part_type} ${part.part_model} (SN${part.part_sn}): ${e.message}`)
            return null
          }
        })
      ).then((deletedIds) => {
        client.invalidateQueries({ queryKey: ['spare-parts'] })
        return deletedIds.filter((id) => id) as number[] // Remove nulls
      }),
  })

  const addManySparesMutation = useMutation({
    mutationFn: async ({ parts }: { parts: ISparePart[] }) =>
      Promise.all(
        parts.map(async (part) => {
          try {
            const newPart = await addSparesMutation.mutateAsync({
              customer_id: part.customer.id,
              //@ts-expect-error needed
              site_id: part.site.id,
              price: part.price ?? undefined,
              part_type: part.part_type,
              part_model: {
                name: part.part_model,
                version: part.part_model_version,
              },
              part_sn: part.part_sn ?? undefined,
            })
            return newPart // Will have a new id
          } catch {
            NotifyFail(`Failed to restore part ${part.id} (${part.part_type} ${part.part_model} ${part.part_sn})`)
          }
        })
      ),
  })

  const bulkUpdateSparePartsMutation = useMutation({
    mutationFn: ({ ids, form }: { ids: number[]; form: ISparePartBulkUpdateForm }) =>
      bulkUpdateSpareParts(ids, { ...form, customer_id: form.customer_id === 0 ? null : form.customer_id }),
    onError: NotifyErrorWrapper('Failed to bulk-update spare parts'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['spare-parts'] })
      NotifySuccess('Spare parts updated')
    },
  })

  return {
    getSparesQuery,
    addSparesMutation,
    updateSparesMutation,
    bulkUpdateSparePartsMutation,
    bulkDeleteSparePartsMutation,
    bulkArchiveSparePartsMutation,
    deleteSparesMutation,
    refurbishSparesMutation,
    deleteManySparesMutation,
    addManySparesMutation,
  }
}
